import { ScanLine } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { type ActivityScanField, type ActivityScanFieldValues } from '@/lib/activity-scan-fields';

interface ScanEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attendeeName: string;
  attendeeEmail?: string;
  fields: ActivityScanField[];
  values: ActivityScanFieldValues;
  onValuesChange: (values: ActivityScanFieldValues) => void;
  isUpdatingExisting?: boolean;
  isSubmitting?: boolean;
  onSubmit: (values: ActivityScanFieldValues) => void;
  onRequestBarcodeScan?: (field: ActivityScanField) => void;
}

export function ScanEntryDialog({
  open,
  onOpenChange,
  attendeeName,
  attendeeEmail,
  fields,
  values,
  onValuesChange,
  isUpdatingExisting,
  isSubmitting,
  onSubmit,
  onRequestBarcodeScan,
}: ScanEntryDialogProps) {
  const setFieldValue = (fieldId: string, value: string | boolean | null) => {
    onValuesChange({ ...values, [fieldId]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isUpdatingExisting ? 'Update check-in details' : 'Complete check-in'}
          </DialogTitle>
          <DialogDescription>
            {attendeeName}
            {attendeeEmail ? ` · ${attendeeEmail}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {fields.map((field) => {
            if (field.type === 'TOGGLE') {
              return (
                <div
                  key={field.id}
                  className="flex items-center justify-between rounded-xl border border-border p-4"
                >
                  <Label htmlFor={`scan-field-${field.id}`} className="flex flex-col gap-1">
                    <span>{field.label}</span>
                    {field.required ? (
                      <span className="text-xs font-normal text-muted-foreground">Required</span>
                    ) : null}
                  </Label>
                  <Switch
                    id={`scan-field-${field.id}`}
                    checked={Boolean(values[field.id])}
                    onCheckedChange={(checked) => setFieldValue(field.id, checked)}
                  />
                </div>
              );
            }

            const isBarcode = field.type === 'BARCODE';
            const rawValue = values[field.id];
            const stringValue: string = typeof rawValue === 'string' ? rawValue : '';

            return (
              <div key={field.id} className="space-y-2">
                <Label htmlFor={`scan-field-${field.id}`}>
                  {field.label}
                  {field.required ? <span className="text-primary"> *</span> : null}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id={`scan-field-${field.id}`}
                    value={stringValue}
                    onChange={(e) => setFieldValue(field.id, e.target.value)}
                    placeholder={
                      field.placeholder || (isBarcode ? 'Scan or type value' : 'Enter value')
                    }
                  />
                  {isBarcode && onRequestBarcodeScan ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => onRequestBarcodeScan(field)}
                    >
                      <ScanLine className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => onSubmit(values)} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isUpdatingExisting ? 'Update scan' : 'Confirm check-in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
